import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Dropdown, Empty, Spin, Typography } from 'antd';
import { BellOutlined, MessageOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import healthRecordsService, {
  type CareRequestMessageNotificationItem,
} from '../../../pages/health-records/services/healthRecords.service';
import { formatRelativeTime } from '../../../utils/date.utils';
import './CareRequestMessageNotificationsBell.scss';

const POLL_MS = 45_000;
export const CARE_REQUEST_MESSAGE_INBOX_REFRESH = 'care-request-message-inbox-refresh';

const { Text } = Typography;

export const CareRequestMessageNotificationsBell: React.FC = () => {
  const navigate = useNavigate();
  const [totalUnread, setTotalUnread] = useState(0);
  const [items, setItems] = useState<CareRequestMessageNotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadInbox = useCallback(async () => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await healthRecordsService.listCareRequestMessageNotifications();
      setTotalUnread(data.totalUnread);
      setItems(data.items);
    } catch (err) {
      console.error('CareRequestMessageNotificationsBell load', err);
      setError('Unable to load unread messages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadInbox();
    const timer = window.setInterval(() => {
      void loadInbox();
    }, POLL_MS);
    const handleRefresh = () => {
      void loadInbox();
    };
    window.addEventListener(CARE_REQUEST_MESSAGE_INBOX_REFRESH, handleRefresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(CARE_REQUEST_MESSAGE_INBOX_REFRESH, handleRefresh);
    };
  }, [loadInbox]);

  const handleOpenItem = (item: CareRequestMessageNotificationItem) => {
    if (!item.serviceRequestId) return;
    navigate(`/service-requests/${item.serviceRequestId}`);

    if (!item.messageId) return;
    void (async () => {
      try {
        await healthRecordsService.markCareRequestMessagesRead(item.serviceRequestId, {
          messageId: item.messageId,
        });
        setItems((prev) => prev.filter((row) => row.messageId !== item.messageId));
        setTotalUnread((prev) => Math.max(0, prev - 1));
        window.dispatchEvent(new Event(CARE_REQUEST_MESSAGE_INBOX_REFRESH));
      } catch (err) {
        console.error('CareRequestMessageNotificationsBell mark read', err);
      }
    })();
  };

  const dropdownContent = (
    <div className="cr-msg-inbox" role="menu">
      <div className="cr-msg-inbox__head">
        <Text strong>Unread care messages</Text>
        {totalUnread > 0 ? (
          <Text type="secondary" className="cr-msg-inbox__count">
            {totalUnread} unread
          </Text>
        ) : null}
      </div>
      {loading && items.length < 1 ? (
        <div className="cr-msg-inbox__loading">
          <Spin size="small" />
        </div>
      ) : error ? (
        <div className="cr-msg-inbox__empty">
          <Text type="danger">{error}</Text>
          <Button type="link" size="small" onClick={() => void loadInbox()}>
            Retry
          </Button>
        </div>
      ) : items.length < 1 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No unread messages"
          className="cr-msg-inbox__empty"
        />
      ) : (
        <ul className="cr-msg-inbox__list">
          {items.map((item) => (
            <li key={item.messageId || `${item.serviceRequestId}-${item.lastMessageAt}`}>
              <button
                type="button"
                className="cr-msg-inbox__card"
                onClick={() => handleOpenItem(item)}
              >
                <span className="cr-msg-inbox__card-icon" aria-hidden>
                  <MessageOutlined />
                </span>
                <span className="cr-msg-inbox__card-body">
                  <span className="cr-msg-inbox__card-title">
                    {item.requestLabel || item.patientName || 'Care request'}
                  </span>
                  <span className="cr-msg-inbox__card-preview">{item.preview || 'New message'}</span>
                  <span className="cr-msg-inbox__card-foot">
                    <span className="cr-msg-inbox__badge">New</span>
                    {item.lastMessageAt ? (
                      <span className="cr-msg-inbox__time">
                        {formatRelativeTime(item.lastMessageAt)}
                      </span>
                    ) : null}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <Dropdown dropdownRender={() => dropdownContent} placement="bottomRight" trigger={['click']}>
      <button
        type="button"
        className="cr-msg-inbox__trigger"
        aria-label={
          totalUnread > 0
            ? `Care request messages, ${totalUnread} unread`
            : 'Care request messages'
        }
      >
        <Badge count={totalUnread} size="small" overflowCount={99}>
          <BellOutlined className="cr-msg-inbox__bell" />
        </Badge>
      </button>
    </Dropdown>
  );
};

export default CareRequestMessageNotificationsBell;
